import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScoreRadar } from "@/components/ScoreCharts";
import EmployeeDevPlanSection from "@/components/EmployeeDevPlanSection";
import { PencilSimple, Check, X, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREA_ORDER = ["INI","AFF","MET","QUA","FOC","ANA","CRE","REL","INF","INT"];
const AREA_LABEL = {
  INI: "Iniziativa", AFF: "Affidabilita", MET: "Metodo", QUA: "Qualita",
  FOC: "Focus", ANA: "Analisi", CRE: "Creativita", REL: "Relazione",
  INF: "Influenza", INT: "Integrita",
};

function bandOf(score) {
  if (score >= 90) return { label: "Dominante", color: "#0071e3" };
  if (score >= 75) return { label: "Solida",    color: "#34c759" };
  if (score >= 60) return { label: "Promettente",color: "#ffcc00" };
  if (score >= 40) return { label: "Potenziale", color: "#ff9500" };
  return              { label: "Critica",   color: "#ff3b30" };
}

// Parse AI HTML into an ordered list of sections keyed by h2
function parseSections(html) {
  if (!html) return [];
  // naive split by <h2>...</h2>
  const re = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
  const parts = [];
  let lastIndex = 0;
  let m;
  const headings = [];
  while ((m = re.exec(html)) !== null) {
    headings.push({ title: m[1].replace(/<[^>]+>/g, "").trim(), start: m.index, end: m.index + m[0].length });
  }
  if (headings.length === 0) return [{ title: null, body: html }];
  // Leading text before first heading (usually empty)
  if (headings[0].start > 0) {
    const pre = html.slice(0, headings[0].start).trim();
    if (pre) parts.push({ title: null, body: pre });
    lastIndex = headings[0].start;
  }
  for (let i = 0; i < headings.length; i++) {
    const h = headings[i];
    const bodyStart = h.end;
    const bodyEnd = i + 1 < headings.length ? headings[i + 1].start : html.length;
    parts.push({ title: h.title, body: html.slice(bodyStart, bodyEnd).trim() });
  }
  return parts;
}

// ── CHART COMPONENTS ────────────────────────────────────────────
// B73: nuovo helper — legge sia il vecchio formato con .score sia il nuovo band-mode
// {label, color, band_midpoint} restituito dal backend serializzato.
function readSlotView(entry) {
  if (!entry) return { label: "N/A", color: "#999", band_midpoint: 0, score: undefined };
  if (typeof entry.band_midpoint === "number" || entry.label) {
    return {
      label: entry.label || "N/A",
      color: entry.color || "#999",
      band_midpoint: typeof entry.band_midpoint === "number" ? entry.band_midpoint : 0,
      score: typeof entry.score === "number" ? entry.score : undefined,
    };
  }
  const s = typeof entry.score === "number" ? entry.score : (typeof entry === "number" ? entry : 0);
  const b = bandOf(s);
  const mid = s >= 90 ? 95 : s >= 75 ? 82 : s >= 60 ? 67 : s >= 40 ? 50 : 20;
  return { label: b.label, color: b.color, band_midpoint: mid, score: s };
}

function ScoreBarRow({ label, slot }) {
  // B73: barre a 5 step discreti (midpoint) + label testuale, nessun %.
  // B78 P0.2: rimossa la spiegazione evidenziale (Sezione 3 = solo radar+
  // barre+label). Signature contract-clean: nessun `explanation` prop.
  return (
    <div className="py-1.5" data-testid={`score-bar-${label}`}>
      <div className="flex items-center gap-3">
        <div className="w-28 text-sm font-medium text-[#0D1F3C] flex-shrink-0">{label}</div>
        <div className="flex-1 relative h-5 bg-[#0D1F3C]/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.max(4, slot.band_midpoint)}%`, backgroundColor: slot.color }}
          />
        </div>
        <Badge
          className="w-[110px] justify-center text-[11px] font-medium border-0"
          style={{ backgroundColor: `${slot.color}1A`, color: slot.color }}
        >
          {slot.label}
        </Badge>
      </div>
    </div>
  );
}

// Unified Profilo Comportamentale — 2 cols: radar (40%) + bars (60%)
// B78 P0.2: rimossi i box con le spiegazioni per dimensione. Sezione 3 =
// solo radar + barre + label di fascia. Le evidenze compaiono in Sezione 6.
function ProfiloComportamentaleBlock({ scores, subjectName }) {
  if (!scores?.areas) return null;
  return (
    <div
      className="grid gap-6 md:grid-cols-[2fr_3fr] grid-cols-1"
      data-testid="profilo-comportamentale-block"
    >
      <div className="rounded-lg border border-[#0D1F3C]/8 bg-white p-4 flex items-center justify-center">
        <div style={{ width: "100%", maxWidth: 360, height: 340 }}>
          <ScoreRadar scores={scores} name={subjectName} size={340} />
        </div>
      </div>
      <div className="rounded-lg border border-[#0D1F3C]/8 bg-white p-4">
        {AREA_ORDER.map((k) => {
          const slot = readSlotView(scores.areas[k]);
          return <ScoreBarRow key={k} label={AREA_LABEL[k]} slot={slot} />;
        })}
      </div>
    </div>
  );
}

function readIndexVal(v) {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (typeof v === "string") { const n = parseFloat(v); return isNaN(n) ? null : n; }
  if (typeof v === "object") {
    // B73 band-mode: {label, color, band_midpoint} — nessun .score visibile a schermo
    if (typeof v.score === "number") return v.score;
    if (typeof v.band_midpoint === "number") return v.band_midpoint;
  }
  return null;
}

// Map a compatibility level to color + normalized label
function compatLevelColor(level) {
  const l = (level || "").toString().toLowerCase();
  if (/^(totale|alta|solida)$/.test(l)) return "#34c759";
  if (/^(parziale|adeguata|funzionale)$/.test(l)) return "#F59E0B";
  if (/^(bassa|critica|fragile)$/.test(l)) return "#EF4444";
  return "#6B7280";
}

// Extract first compatibility level word from HTML (uppercase or capitalized)
function extractCompatLevel(html) {
  if (!html) return null;
  const LEVELS = ["TOTALE","ALTA","PARZIALE","BASSA","SOLIDA","ADEGUATA","CRITICA","FUNZIONALE","FRAGILE"];
  const text = html.replace(/<[^>]+>/g, " ");
  for (const L of LEVELS) {
    const re = new RegExp(`\\b${L}\\b`);
    if (re.test(text)) return L.charAt(0) + L.slice(1).toLowerCase();
  }
  return null;
}

// Remove any AI-generated mega badge around the compatibility level.
// Works on nested structures by iteratively stripping any <div>...</div> whose
// inner text (tags removed) equals one of the label/level words, then cleaning
// empty wrappers left behind.
function stripCompatMegaBadge(html) {
  if (!html) return html;
  const LABELS = new Set([
    "COMPATIBILITÀ","COMPATIBILITA","ADEGUATEZZA",
    "TOTALE","ALTA","PARZIALE","BASSA",
    "SOLIDA","ADEGUATA","CRITICA","FUNZIONALE","FRAGILE",
  ]);
  let prev = "";
  let current = html;
  let safety = 10;
  while (prev !== current && safety-- > 0) {
    prev = current;
    current = current.replace(/<div\b[^>]*>([\s\S]*?)<\/div>/gi, (match, inner) => {
      const text = inner.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim().toUpperCase();
      if (LABELS.has(text)) return "";
      return match;
    });
    // Strip now-empty div wrappers
    current = current.replace(/<div\b[^>]*>\s*<\/div>/gi, "");
  }
  return current;
}

// Indici Sintetici — 3 cards grid (allineato al PDF: solo label, no numeri).
// B74 PARTE 3: aggiunta spiegazione statica sotto la tagline, SOLO per report candidato.
function IndicesCardsBlock({ indices, scores, entityType = "candidate" }) {
  const legacy = {
    trasparenza: readIndexVal(scores?.transparency) ?? readIndexVal(scores?.trasparenza),
    stabilita:   readIndexVal(scores?.stability)    ?? readIndexVal(scores?.stabilita),
    coerenza:    readIndexVal(scores?.coherence)    ?? readIndexVal(scores?.coerenza),
  };
  const fromIndices = {
    trasparenza: readIndexVal(indices?.trasparenza),
    stabilita:   readIndexVal(indices?.stabilita),
    coerenza:    readIndexVal(indices?.coerenza),
  };
  const src = {
    trasparenza: fromIndices.trasparenza ?? legacy.trasparenza,
    stabilita:   fromIndices.stabilita   ?? legacy.stabilita,
    coerenza:    fromIndices.coerenza    ?? legacy.coerenza,
  };
  if (src.trasparenza == null && src.stabilita == null && src.coerenza == null) return null;
  // B78 retro: gate della spiegazione estesa PER NOME dell'indice, non per
  // tipo di report. Se in futuro `cards` include indici con altri nomi
  // (senza `long` definito), semplicemente non riceveranno il paragrafo.
  const cards = [
    {
      key: "trasparenza",
      label: "Trasparenza",
      desc: "Coerenza tra auto-percezione e comportamento espresso",
      long: "Racconta quanto la persona si mostra in modo aderente a come agisce davvero: quanto cio che dichiara di se combacia con il comportamento che esprime nelle situazioni. Un profilo trasparente ha risposto con spontaneita; quando la trasparenza e piu bassa conviene leggere il resto con cautela e approfondirlo in colloquio.",
    },
    {
      key: "stabilita",
      label: "Stabilita",
      desc: "Uniformita delle risposte nelle situazioni simili",
      long: "Riflette quanto le risposte restano costanti di fronte a situazioni simili tra loro. Racconta la prevedibilita del comportamento: una stabilita alta indica reazioni coerenti nel tempo, una piu bassa segnala oscillazioni da approfondire.",
    },
    {
      key: "coerenza",
      label: "Coerenza",
      desc: "Allineamento valoriale e decisionale",
      long: "Indica quanto i valori a cui la persona da importanza si traducono in scelte concrete nella stessa direzione. Aiuta a capire se intenzioni e decisioni vanno d'accordo, o se emergono tensioni tra cio che si dichiara e cio che si sceglie.",
    },
  ];
  return (
    <div className="grid gap-4 md:grid-cols-3 grid-cols-1" data-testid="indices-cards-block">
      {cards.map((c) => {
        const raw = src[c.key];
        const v = raw == null ? 0 : Math.round(raw);
        const b = bandOf(v);
        return (
          <div
            key={c.key}
            className="rounded-xl p-5 border shadow-sm flex flex-col gap-2"
            style={{ borderColor: `${b.color}40`, backgroundColor: `${b.color}1A` }}
            data-testid={`indice-card-${c.key}`}
          >
            <div className="text-sm font-medium text-[#0D1F3C]/70">{c.label}</div>
            {/* B73: nessun % esposto. Il badge di fascia e la sintesi principale. */}
            <Badge
              className="w-fit text-[13px] font-semibold border-0 px-3 py-1"
              style={{ backgroundColor: b.color, color: "#fff" }}
            >
              {b.label}
            </Badge>
            <p className="text-xs text-[#0D1F3C]/60 italic leading-relaxed mt-1">{c.desc}</p>
            {c.long && (
              <p
                className="text-[12px] text-[#0D1F3C]/75 leading-relaxed mt-2"
                data-testid={`indice-long-${c.key}`}
              >
                {c.long}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── MAIN COMPONENT ──────────────────────────────────────────────
export default function UnifiedReportView({ report, subject, entityType = "candidate", entityId, onNotesUpdated }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(subject?.hr_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  const sections = useMemo(() => parseSections(report?.html || ""), [report?.html]);
  const scores = report?.scores || subject?.scores;
  // B75: evidenze deterministiche + guardrail (backend). Fallback safe: {}.
  const dimensionExplanations = report?.dimension_explanations || {};

  // B74 PARTE 2 — Se l'HTML AI NON contiene una sezione "Indici Sintetici" ma
  // abbiamo comunque dati indici (transparency/stability/coherence), rendiamo
  // comunque il blocco alla fine per rispecchiare il PDF.
  const hasIndiciSectionInHtml = useMemo(
    () => sections.some((s) => s?.title && /indici.*sint|indicatori.*trasparenza/i.test(s.title.toLowerCase())),
    [sections]
  );
  const hasIndiciData = useMemo(() => {
    const s = scores || {};
    const readAny = (v) => v != null && (
      typeof v === "number" ||
      (typeof v === "object" && (typeof v.score === "number" || typeof v.band_midpoint === "number"))
    );
    return readAny(s.transparency) || readAny(s.stability) || readAny(s.coherence)
      || readAny(s.trasparenza)   || readAny(s.stabilita) || readAny(s.coerenza)
      || readAny(s.indices?.trasparenza) || readAny(s.indices?.stabilita) || readAny(s.indices?.coerenza);
  }, [scores]);

  const overallSlot = useMemo(() => {
    if (!scores?.areas) return { label: "N/A", color: "#999", band_midpoint: 0 };
    // Preferisce avg_view dal backend se presente su subject
    if (subject?.avg_view) return subject.avg_view;
    const mids = AREA_ORDER.map((k) => readSlotView(scores.areas[k]).band_midpoint);
    const m = mids.reduce((a, b) => a + b, 0) / mids.length;
    // Mappa il midpoint alla stessa fascia (i 5 midpoint 20/50/67/82/95 sono già in fascia)
    return bandOf(m);
  }, [scores, subject]);

  const overallBand = overallSlot;

  const saveNotes = async () => {
    if (!entityId) return;
    setSavingNotes(true);
    try {
      const endpoint = entityType === "employee"
        ? `${API}/employees/${entityId}`
        : `${API}/candidates/${entityId}`;
      await axios.put(endpoint, { hr_notes: notesDraft }, { withCredentials: true });
      toast.success("Note aggiornate");
      setEditingNotes(false);
      if (onNotesUpdated) onNotesUpdated(notesDraft);
    } catch {
      toast.error("Errore salvataggio note");
    } finally {
      setSavingNotes(false);
    }
  };

  if (!report && !scores) {
    return (
      <div className="py-12 text-center text-[#0D1F3C]/40 text-sm" data-testid="unified-report-empty">
        Report non ancora disponibile
      </div>
    );
  }

  const subjectName = subject ? `${subject.first_name || ""} ${subject.last_name || ""}`.trim() : "";

  return (
    <div className="space-y-6" data-testid="unified-report-view">
      {/* Cover / header card */}
      <Card className="border-[#0D1F3C]/8 bg-gradient-to-br from-[#0D1F3C] to-[#1B4FD8] text-white" data-testid="report-cover">
        <CardContent className="p-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-white/60">Report Selly</div>
            <h1 className="text-2xl font-semibold mt-1">{subjectName || report?.name}</h1>
            <p className="text-sm text-white/80 mt-1">
              {entityType === "employee" ? "Valutazione comportamentale dipendente"
                : entityType === "manager"  ? "Valutazione manageriale"
                : "Valutazione candidato"}
              {report?.created_at ? ` · ${new Date(report.created_at).toLocaleDateString("it-IT")}` : ""}
            </p>
          </div>
          {scores && (
            <div className="text-center flex flex-col items-end gap-1">
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wide">Profilo</div>
                <div className="text-xl font-bold mt-1" style={{ color: overallBand.color }}>{overallBand.label}</div>
              </div>
              {report?.engine_version && (
                <div
                  className="text-[10px] text-white/50 font-mono"
                  data-testid="report-engine-version"
                  title="Motore che ha generato questo report"
                >
                  Motore {report.engine_version}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* B82 P0.1(follow-up) — Badge report pre-HMAC — SOLO admin */}
      {isAdmin && report && report.scoring_version !== "b72.3" && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 flex items-start gap-3" data-testid="pre-hmac-warning">
          <Warning size={22} className="text-red-600 shrink-0 mt-0.5" weight="fill" />
          <div className="flex-1">
            <div className="font-semibold text-red-900 text-sm">Calcolo non affidabile — da rigenerare</div>
            <p className="text-xs text-red-800 mt-1">
              Questo report è stato generato prima del fix del 21/07/2026 sull&apos;ordine delle
              opzioni del questionario. I punteggi mostrati possono non corrispondere alle
              risposte reali della persona. Se il documento è già stato consegnato al cliente,
              valuta con lui come procedere prima di rigenerare.
            </p>
          </div>
        </div>
      )}

      {/* HR Notes card on top */}
      {entityId && (
        <Card className="border-[#F59E0B]/30 bg-[#FEF3C7]/40" data-testid="hr-notes-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-[#0D1F3C] mb-1">Note del Responsabile HR</h3>
                {editingNotes ? (
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={4}
                    placeholder="Aggiungi il tuo contesto (assunto per ruolo X, trasferito da reparto Y, in affiancamento a Z...)"
                    data-testid="hr-notes-textarea"
                  />
                ) : (
                  <p className="text-sm text-[#0D1F3C]/80 whitespace-pre-wrap">
                    {subject?.hr_notes || <span className="italic text-[#0D1F3C]/40">Nessuna nota inserita. Usa questo spazio per arricchire il contesto del profilo.</span>}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                {editingNotes ? (
                  <>
                    <Button size="sm" onClick={saveNotes} disabled={savingNotes} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="hr-notes-save-btn">
                      <Check size={14} className="mr-1" />{savingNotes ? "..." : "Salva"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditingNotes(false); setNotesDraft(subject?.hr_notes || ""); }} data-testid="hr-notes-cancel-btn">
                      <X size={14} />
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => { setNotesDraft(subject?.hr_notes || ""); setEditingNotes(true); }} data-testid="hr-notes-edit-btn">
                    <PencilSimple size={14} className="mr-1" />Modifica
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rendered AI sections WITH inline charts after specific headings */}
      <div>
      {sections.map((sec, idx) => {
        if (!sec.title) {
          // pre-heading content (rare) — render as prose
          return (
            <div key={idx} className="report-prose" dangerouslySetInnerHTML={{ __html: sec.body }} />
          );
        }
        const normalized = sec.title.toLowerCase();
        const isProfiloSec = /profilo.*comportamentale|scheda.*visiv/i.test(normalized);
        const isIndiciSec  = /indici.*sint|indicatori.*trasparenza/i.test(normalized);
        const isDettaglioSec = /dettaglio.*aree|punteggi.*per.*area|punteggi.*aree/i.test(normalized);
        const isCompatSec = /^\s*2\./.test(sec.title) && /(compatibilit|adeguatezza).*ruolo/i.test(normalized);

        // Legacy reports may still emit "Dettaglio Aree Comportamentali" — skip it entirely
        // since its bars are now merged into Sezione 3 (Profilo Comportamentale).
        if (isDettaglioSec) return null;

        // Remove inline placeholders (charts-placeholder / indices-placeholder / areas-grid-placeholder)
        // that the AI may emit inside the section body, so we don't render empty divs.
        let cleanBody = (sec.body || "")
          .replace(/<div[^>]*id=["'](?:charts|indices|areas-grid)-placeholder["'][^>]*>\s*<\/div>/gi, "")
          .trim();

        // Sezione 2: strip AI's mega compatibility badge and render a compact inline indicator instead
        let compatIndicator = null;
        if (isCompatSec) {
          const level = subject?.match_score || extractCompatLevel(cleanBody);
          cleanBody = stripCompatMegaBadge(cleanBody).trim();
          if (level) {
            const color = compatLevelColor(level);
            compatIndicator = (
              <div className="flex items-center gap-2 text-sm" data-testid="compat-indicator">
                <span className="text-[#0D1F3C]/60 font-medium">Giudizio:</span>
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="font-semibold text-[15px]" style={{ color }}>
                  {level}
                </span>
              </div>
            );
          }
        }

        // BUG-32: Sezione 9 "Piano di Leadership/Sviluppo" — il body AI contiene solo
        // <div id="dev-plan-placeholder"></div>. Renderizziamo direttamente
        // <EmployeeDevPlanSection /> come React child (NIENTE portal: il portal
        // viene cancellato dal dangerouslySetInnerHTML del parent).
        const isDevPlanSec = /piano.*(leadership|sviluppo)/i.test(normalized) ||
                             /dev-plan-placeholder/i.test(cleanBody);
        const showDevPlan = isDevPlanSec && entityId &&
                            ["employee", "manager", "management"].includes(entityType);
        if (showDevPlan) {
          // Sostituiamo cleanBody col render React diretto (no innerHTML)
          cleanBody = "";
        }

        return (
          <section key={idx} className={`space-y-3 ${showDevPlan ? "pb-8" : ""}`} data-testid={`report-section-${idx}`}>
            <h2 className="text-lg font-semibold text-[#0D1F3C] pb-1 border-b border-[#0D1F3C]/10">{sec.title}</h2>

            {compatIndicator}

            {/* Section AI body (excluding empty placeholders) */}
            {cleanBody && (
              <div className="report-prose" dangerouslySetInnerHTML={{ __html: cleanBody }} />
            )}

            {/* BUG-32: render diretto React per la sezione Piano di Leadership/Sviluppo */}
            {showDevPlan && <EmployeeDevPlanSection employeeId={entityId} />}

            {/* Inline chart augmentation — added AFTER the text of each section */}
            {isProfiloSec && scores?.areas && (
              <div style={{ overflow: "hidden", pageBreakAfter: "always", breakAfter: "always" }}>
                <ProfiloComportamentaleBlock
                  scores={scores}
                  subjectName={subjectName}
                />
              </div>
            )}
            {isIndiciSec && <IndicesCardsBlock indices={scores?.indices} scores={scores} entityType={entityType} />}
          </section>
        );
      })}
      </div>

      {/* B74 PARTE 2 — Fallback: se la sezione "Indici Sintetici" non c'è
          nell'HTML AI ma abbiamo dati, la aggiungiamo qui (allineato al PDF). */}
      {!hasIndiciSectionInHtml && hasIndiciData && (
        <section className="space-y-3" data-testid="report-section-indici-fallback">
          <h2 className="text-lg font-semibold text-[#0D1F3C] pb-1 border-b border-[#0D1F3C]/10">
            4. Indici Sintetici
          </h2>
          <IndicesCardsBlock indices={scores?.indices} scores={scores} entityType={entityType} />
        </section>
      )}

      {/* Single disclaimer at the very bottom */}
      <div
        className="text-xs text-[#0D1F3C]/50 italic border-t border-[#0D1F3C]/10 pt-4 leading-relaxed"
        data-testid="report-disclaimer"
      >
        I risultati prodotti da Selly hanno finalita orientativa e di supporto decisionale.
        Non si sostituiscono al giudizio professionale del Responsabile HR. Selly supporta —
        non sostituisce — la valutazione umana.
      </div>

      {/* Prose styling scoped to .report-prose */}
      <style>{`
        .report-prose { color: #0D1F3C; font-size: 14px; line-height: 1.75; }
        .report-prose h3 { font-size: 15px; font-weight: 600; color: #0D1F3C; margin-top: 1.25em; margin-bottom: .4em; }
        .report-prose p { margin: 0 0 .75em 0; }
        .report-prose ul, .report-prose ol { margin: 0 0 .9em 1.2em; padding: 0; }
        .report-prose li { margin-bottom: .3em; }
        .report-prose strong { color: #0D1F3C; font-weight: 600; }
        .report-prose em { color: #0D1F3C99; }
        .report-prose table { border-collapse: collapse; margin: .5em 0; width: 100%; }
        .report-prose td, .report-prose th { padding: 6px 10px; border: 1px solid #0D1F3C1A; font-size: 13px; }
        /* hide any stale inline disclaimer the AI might leak */
        .report-prose p[class*="disclaimer"], .report-prose div[class*="disclaimer"] { display: none; }
      `}</style>
    </div>
  );
}
